import { useEffect, useState } from 'react';
import { ReactComponent as CalendarSvg } from '../../../../assets/svg/calendar.svg';
import { ReactComponent as DishSvg } from '../../../../assets/svg/dish.svg';
import { ReactComponent as TimerSvg } from '../../../../assets/svg/timer.svg';
import RingProgress from '../../../../components/progress/ring';
import Bar from '../../../../components/progress/bar';
import request from '../../../../lib/request';

const calculatePercentage = (value, total) => {
  return Math.floor((value / total) * 100);
};

const calculateDailyTotalWorkoutHours = (list) => {
  const seconds = list.reduce((acc, cur) => acc + (cur.duration ?? 0), 0);
  return Math.floor(seconds / 60);
};

const calculateDailyMacronutrients = (ingredients) => {
  const protein = ingredients.reduce(
    (acc, cur) => acc + (cur.proteins ?? 0),
    0
  );
  const carbs = ingredients.reduce(
    (acc, cur) => acc + (cur.carbohydrates ?? 0),
    0
  );
  const fat = ingredients.reduce((acc, cur) => acc + (cur.fats ?? 0), 0);
  return { protein, carbs, fat };
};

const Goal = ({ selectedDate, currentWeeks, onClick }) => {
  const [goalExist, setGoalExist] = useState(false);
  const [goal, setGoal] = useState({
    dailyCalorie: 0,
    dailyWorkoutHours: 0,
    weeklyWorkoutDays: 0,
  });
  const [dailyTotalCalorie, setDailyTotalCalorie] = useState(0);
  const [dailyProtein, setDailyProtein] = useState(0);
  const [dailyCarbs, setDailyCarbs] = useState(0);
  const [dailyFat, setDailyFat] = useState(0);
  const [dailyTotalWorkoutHours, setDailyTotalWorkoutHours] = useState(0);
  const [weeklyTotalWorkoutDays, setWeeklyTotalWorkoutDays] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getUserGoal();
  }, []);

  useEffect(() => {
    if (currentWeeks?.length) {
      getWorkoutTracker(currentWeeks[0], currentWeeks[currentWeeks.length - 1]);
    }
  }, [currentWeeks]);

  useEffect(() => {
    getTodayTotalCalories(selectedDate);
    getWorkoutLog(selectedDate);
  }, [selectedDate]);

  const getUserGoal = async () => {
    try {
      setLoading(true);
      const { result } = await request.get('/user/goal');
      if (result?.length) {
        setGoal(result[0]);
        setGoalExist(true);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getTodayTotalCalories = async (date) => {
    try {
      const { result } = await request.get(`/meal/today?date=${date}`);
      if (result) {
        setDailyTotalCalorie(result.totalCalories);

        let protein = 0;
        let carbs = 0;
        let fat = 0;

        for (const item of result.list) {
          const {
            protein: itemProtein,
            carbs: itemCarbs,
            fat: itemFat,
          } = calculateDailyMacronutrients(item.ingredients);
          protein += itemProtein;
          carbs += itemCarbs;
          fat += itemFat;
        }

        setDailyProtein(protein.toFixed(1));
        setDailyCarbs(carbs.toFixed(1));
        setDailyFat(fat.toFixed(1));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getWorkoutLog = async (date) => {
    try {
      const startDate = new Date(`${date}T00:00:00`).toISOString();
      const endDate = new Date(`${date}T23:59:59.999`).toISOString();

      const { result } = await request.get(
        `/workout/log?date=${date}&startDate=${startDate}&endDate=${endDate}`
      );
      if (result) {
        const mins = calculateDailyTotalWorkoutHours(result);
        setDailyTotalWorkoutHours(mins);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getWorkoutTracker = async (startDate, endDate) => {
    try {
      const { result } = await request.get(
        `/workout?startDate=${startDate}&endDate=${endDate}`
      );
      if (result && Array.isArray(result)) {
        const counts = new Set(result.map((item) => item.logDate));
        setWeeklyTotalWorkoutDays(counts.size);
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div>
      <button
        className={`rounded-xl px-1 py-2 w-full bg-secondary flex items-center justify-center ${
          goalExist ? 'aspect-square' : 'aspect-video'
        }`}
        onClick={onClick}
      >
        {loading ? (
          <div className='flex items-center justify-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-t-2 border-white'></div>
          </div>
        ) : goalExist ? (
          <div className='w-full'>
            <div className='mb-4 w-full'>
              <div className='flex items-center justify-evenly'>
                <RingProgress
                  percentage={calculatePercentage(
                    dailyTotalCalorie,
                    goal.dailyCalorie
                  )}
                  color='#F8A171'
                  text={`${dailyTotalCalorie} / ${goal.dailyCalorie} Cal`}
                  size={142}
                  title='Daily Calorie'
                  icon={<DishSvg className='size-3' />}
                />
                <div className='flex flex-col gap-2 w-[140px]'>
                  <Bar
                    title='Protein'
                    percentage={calculatePercentage(
                      Number(dailyProtein),
                      Number(goal.dailyProtein)
                    )}
                    current={Number(dailyProtein)}
                    total={Number(goal.dailyProtein)}
                    color='#FF6B6B'
                    unit='g'
                  />
                  <Bar
                    title='Carbs'
                    percentage={calculatePercentage(
                      Number(dailyCarbs),
                      Number(goal.dailyCarbs)
                    )}
                    current={Number(dailyCarbs)}
                    total={Number(goal.dailyCarbs)}
                    color='#4ECDC4'
                    unit='g'
                  />
                  <Bar
                    title='Fats'
                    percentage={calculatePercentage(
                      Number(dailyFat),
                      Number(goal.dailyFat)
                    )}
                    current={Number(dailyFat)}
                    total={Number(goal.dailyFat)}
                    color='#FFD93D'
                    unit='g'
                  />
                </div>
              </div>
              <div className='flex items-center justify-evenly'>
                <RingProgress
                  percentage={calculatePercentage(
                    dailyTotalWorkoutHours,
                    goal.dailyWorkoutHours
                  )}
                  color='#9B59B6'
                  text={`${dailyTotalWorkoutHours} / ${goal.dailyWorkoutHours} min`}
                  size={142}
                  title='Daily Workout'
                  icon={<TimerSvg className='size-3' />}
                />
                <RingProgress
                  percentage={calculatePercentage(
                    weeklyTotalWorkoutDays,
                    goal.weeklyWorkoutDays
                  )}
                  color='#3498DB'
                  text={`${weeklyTotalWorkoutDays} / ${goal.weeklyWorkoutDays} days`}
                  size={142}
                  title='Weekly Workout'
                  icon={<CalendarSvg className='size-3' />}
                />
              </div>
            </div>
            <div className='text-xs text-white/50 underline'>Click to edit</div>
          </div>
        ) : (
          <div className='font-bold text-xl py-4 text-white'>Set Your Goal</div>
        )}
      </button>
    </div>
  );
};

export default Goal;
